import { FC, ReactElement, useRef, useState } from "react";
import { Button, Box, Typography, Divider, Stack } from "@mui/material";
import { FaCaretDown } from "react-icons/fa6";

import { MenuPopover, Avatar, TenantPicker, ClientPicker } from "@/components";
import { LogoutItem } from "./logout-item";
import { useAppSelector } from "@/store";
import { useGetAllClientsQuery } from "@/services";
import { roles } from "@/roles";

interface AccountPopoverProps {
  isSidebarExtended?: boolean;
  canSelectTenants?: boolean;
  canSelectClients?: boolean;
  clientPickerTenantId?: string;
  isValidateRoute?: boolean;
}

const AccountPopover: FC<AccountPopoverProps> = ({
  isSidebarExtended = true,
  canSelectTenants = false,
  canSelectClients = false,
  clientPickerTenantId = "",
  isValidateRoute = false,
}): ReactElement => {
  const user = useAppSelector((state) => state.auth.user);
  const { companyName } = useAppSelector((state) => state.settings);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);

  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  useGetAllClientsQuery(
    { searchKeyword: "", pageNo: 1, pageSize: 25, tenantId: clientPickerTenantId },
    { 
      skip: !canSelectClients || !clientPickerTenantId,
      refetchOnMountOrArgChange: true,
    }
  );

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const pickerStyles = {
    width: "100%",
    border: "none",
    justifyContent: "flex-start",
    padding: "6px 8px",
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
      border: "none",
    },
    "&[aria-expanded='true']": {
      border: "none",
      boxShadow: "none",
      backgroundColor: "rgba(0, 0, 0, 0.08)",
    },
  };

  const isRiffsClientAccount =
    user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT;

  const displaySubtitle =
    isRiffsClientAccount
      ? "Riffs Client"
      : selectedClient?.firstName || selectedTenant?.companyName || companyName || "No Company";

  return (
    <>
      <Button
        ref={anchorRef}
        onClick={handleOpen}
        fullWidth={isSidebarExtended}
        sx={{
          padding: "8px 12px",
          boxShadow: "none",
          minWidth: 0,
          justifyContent: isSidebarExtended ? "flex-start" : "center",
          height: isSidebarExtended ? 56 : "auto",
          ...(open && {
            "&:before": {
              zIndex: 1,
              content: "''",
              width: "100%",
              height: "100%",
              borderRadius: 1,
              position: "absolute",
              border: "1px solid #0B494B",
              boxShadow: "0px 0px 0px 4px #0B494B3D",
            },
          }),
        }}
      >
        <Avatar url={user?.profileImage || undefined} width={isSidebarExtended ? 40 : 28} height={isSidebarExtended ? 40 : 28} />
        {isSidebarExtended && (
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            flex={1}
            ml={1.5}
            overflow="hidden"
          >
            <Box sx={{ textAlign: "left", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography color={"#052223"} fontWeight={500} fontSize={14} lineHeight={"20px"} noWrap>
                {`${user?.firstName ?? ""} ${user?.lastName ?? ""}`}
              </Typography>
              <Typography color={"#052223"} fontWeight={400} fontSize={12} lineHeight={"16px"} noWrap sx={{ opacity: 0.7 }}>
                {displaySubtitle}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" ml={1}>
              <FaCaretDown color="#8F92A166" />
            </Box>
          </Stack>
        )}
      </Button>
      {/* MENU POPOVER */}
      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current as Element}
        sx={{ width: 280, borderRadius: "8px" }}
      >
        <Stack sx={{ my: 1.5, px: 2 }} spacing={1}>
          {/* Pickers */}
          {((canSelectTenants || canSelectClients) && !isValidateRoute) && (
            <>
              {canSelectTenants && (
                 <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, pl: 1, display: 'block', fontWeight: 600, fontSize: '0.75rem' }}>
                      TENANT
                    </Typography>
                    <TenantPicker sx={pickerStyles} />
                 </Box>
              )}
              
              {canSelectClients && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, pl: 1, display: 'block', fontWeight: 600, fontSize: '0.75rem' }}>
                      CLIENT
                    </Typography>
                    <ClientPicker tenantId={clientPickerTenantId} sx={pickerStyles} />
                  </Box>
              )}
              <Divider />
            </>
          )}

          <LogoutItem navbar />
        </Stack>
      </MenuPopover>
    </>
  );
};

export default AccountPopover;
